import { CalendarIcon, X } from 'lucide-react';
import * as React from 'react';

import { DimeButton, type ButtonSize } from './DimeButton.js';
import { DimeCalendar } from './DimeCalendar.js';
import { DimePopover, DimePopoverContent, DimePopoverTrigger } from './DimePopover.js';
import { useAutoFocusOnFill } from './hooks/use-auto-focus-on-fill.js';
import { cn } from './lib/utils.js';
import {
  formatLocalDate,
  localDateToYmd,
  validDateOrUndefined,
  ymdToLocalNoon,
} from './lib/ymd-date.js';

interface DimeDatePickerBaseProps {
  mode?: 'single';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Forwarded to the trigger button -- defaults to 'default' (the original w-64 footprint).
   *  Pass 'sm' to match a row of size="sm" filter/toolbar buttons (e.g. the Price Sheet
   *  selectors). */
  size?: ButtonSize;
  /** Prefixes a selected date ("Effective: Jan 5, 2026") the way a filter combobox's own trigger
   *  label prefixes its selected value -- unset, the trigger falls back to `placeholder` alone,
   *  same as before this prop existed. */
  label?: string;
  /** Shows a small "x" next to a selected date that resets it to `undefined` -- opt-in, off by
   *  default, matching `DimeComboboxInput`'s own `showClear` for the same "clear this filter"
   *  convenience. Rendered as a sibling of the trigger button, not nested inside it (a button
   *  inside a button is invalid HTML and breaks keyboard/AT navigation). */
  showClear?: boolean;
  /** Stretches the trigger to its container instead of the fixed w-64 footprint. */
  fullWidth?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  /** Focuses the trigger and opens the calendar once when it becomes true, same contract as `DimeAutocomplete`'s. */
  autoFocusOnFill?: boolean;
  calendarProps?: Omit<React.ComponentProps<typeof DimeCalendar>, 'mode' | 'selected' | 'onSelect'>;
}

/** The default: a `Date` in, a `Date` out. */
export interface DimeDatePickerSingleProps extends DimeDatePickerBaseProps {
  valueFormat?: undefined;
  value?: Date;
  defaultValue?: Date;
  onValueChange?: (date: Date | undefined) => void;
}

/** A calendar day as `YYYY-MM-DD` in and out, so no zone ever sees it. Callers pass a
 *  `CalendarDate` (a branded `string`); the design system stays free of the branded type. */
export interface DimeDatePickerYmdProps extends DimeDatePickerBaseProps {
  valueFormat: 'ymd';
  /** A value that is not a real `YYYY-MM-DD` (`2026-02-30`, an ISO instant) shows the placeholder,
   *  the same as no value; validate it upstream if that should be an error. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (ymd: string | undefined) => void;
}

export type DimeDatePickerProps = DimeDatePickerSingleProps | DimeDatePickerYmdProps;

/** The calendar and the trigger text always work in a local `Date`; a ymd string becomes local noon,
 *  and an Invalid Date reads as no date. */
function toPickerDate(value: Date | string | undefined): Date | undefined {
  return typeof value === 'string' ? ymdToLocalNoon(value) : validDateOrUndefined(value);
}

export function DimeDatePicker(props: Readonly<DimeDatePickerProps>) {
  const {
    value,
    defaultValue,
    placeholder = 'Pick a date',
    disabled,
    className,
    id,
    size = 'default',
    label,
    showClear = false,
    fullWidth = false,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
    autoFocusOnFill,
    calendarProps,
  } = props;
  // Whether the caller passed `value` at all -- not whether it's currently set. A controlled
  // caller (e.g. the Price Sheet's Effective Date filter) starts `value` at `undefined`, so
  // checking `value === undefined` per call (the old approach) misclassified the first pick as
  // uncontrolled, latched it into `internal`, and never looked at `value` again -- Clear's
  // `onValueChange(undefined)` nulled the caller's state but this kept showing the stale date.
  const isControlled = 'value' in props;
  const [open, setOpen] = useAutoFocusOnFill(
    id ?? '',
    autoFocusOnFill === true && disabled !== true,
  );
  const [internal, setInternal] = React.useState<Date | undefined>(() =>
    toPickerDate(defaultValue),
  );
  // Memoised so a controlled ymd string keeps one Date per value instead of a new one each render.
  const controlled = React.useMemo(() => toPickerDate(value), [value]);
  const selected = isControlled ? controlled : internal;
  const displayText = selected
    ? label
      ? `${label}: ${formatLocalDate(selected)}`
      : formatLocalDate(selected)
    : placeholder;

  function handleSelect(date: Date | undefined) {
    if (!isControlled) setInternal(date);
    if (props.valueFormat === 'ymd') props.onValueChange?.(date && localDateToYmd(date));
    else props.onValueChange?.(date);
    setOpen(false);
  }

  return (
    <div className={cn('relative', fullWidth ? 'flex w-full' : 'inline-flex')}>
      <DimePopover open={open} onOpenChange={setOpen}>
        <DimePopoverTrigger asChild>
          <DimeButton
            id={id}
            variant="outline"
            size={size}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            // Only set when `label` is passed, and always mirrors the visible text exactly (not
            // just the bare label) -- otherwise this stays unset so an external
            // <DimeFieldLabel htmlFor={id}> (every other caller's pattern) keeps naming the button
            // via label association, same as before this prop existed.
            aria-label={label ? displayText : undefined}
            className={cn(
              fullWidth ? 'w-full' : size === 'default' ? 'w-64' : 'w-auto',
              'justify-start text-left font-normal',
              // Muted only for a bare placeholder -- a `label` (e.g. "Effective Date") always
              // shows a real field name, not a placeholder, so it stays at full strength like
              // every sibling filter button (this was the only muted control in the Price Sheet's
              // filter row, reading as disabled when it wasn't).
              !selected && !label && 'text-muted-foreground',
              showClear && selected && 'pr-7',
              className,
            )}
          >
            <CalendarIcon className="mr-2 size-4 shrink-0" />
            <span className="truncate">{displayText}</span>
          </DimeButton>
        </DimePopoverTrigger>
        <DimePopoverContent className="w-auto p-0" align="start">
          <DimeCalendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={handleSelect}
            {...calendarProps}
          />
        </DimePopoverContent>
      </DimePopover>
      {showClear && selected && !disabled && (
        // A sibling of the trigger button, not nested inside it -- a button inside a button is
        // invalid HTML and breaks keyboard/AT navigation. stopPropagation keeps this click from
        // also toggling the popover trigger underneath it.
        <button
          type="button"
          aria-label={`Clear ${label ?? 'date'}`}
          className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          onClick={(event) => {
            event.stopPropagation();
            handleSelect(undefined);
          }}
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
